import * as THREE from "three";
import { isIOS } from "../../utils/is-mobile";

/**
 *
 */
class CameraCapture {
  /**
   *
   * @param {*} mainRenderer
   * @param {*} container
   */
  constructor(mainRenderer, container, scene) {
    this.mainRenderer = mainRenderer;
    this.mainRenderer.autoClear = false;

    this.captureRenderer = new THREE.WebGLRenderer({ alpha: true });
    this.captureRenderer.setSize(window.innerWidth, window.innerHeight);
    this.captureRenderer.domElement.style.margin = "auto";
    document.body.appendChild(this.captureRenderer.domElement);

    this.horizontallyMirrored = false;

    this.captureScene = new THREE.Scene();
    this.captureScene.name = "capture-scene";
    this.captureScene.background = scene.background;

    this.video = document.createElement("video");
    this.video.setAttribute("autoplay", true);
    this.video.setAttribute("playsinline", true);
    this.video.style.display = "none";

    this.texture = new THREE.VideoTexture(this.video);
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.format = THREE.RGBAFormat;

    this.video.addEventListener(
      "loadedmetadata",
      e => {
        this.videoMesh.visible = true;
        this.aspectRatioH = this.texture.image.videoWidth / this.texture.image.videoHeight;
        this.aspectRatioV = this.texture.image.videoHeight / this.texture.image.videoWidth;

        this.videoWidth = this.video.videoWidth;
        this.videoHeight = this.video.videoHeight;

        this.fitToSize(container.innerWidth, isIOS() ? screen.height : container.innerHeight);
        // AFRAME.scenes[0].object3D.entered = true;
        // Start fade-in effect once video is ready
        this.fadeInVideo();
      },
      false
    );

    this.cameraWebcam = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);

    // Initially set opacity to 0 for fade-in effect
    this.videoMaterial = new THREE.MeshBasicMaterial({ map: this.texture, opacity: 0, transparent: true });

    this.videoMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.videoMaterial);
    this.videoMesh.visible = false; // Initially hidden

    this.captureScene.add(this.videoMesh);

    this.scene = scene;
  }

  /**
   * Smooth fade-in effect
   */
  fadeInVideo() {
    const fadeDuration = 1000; // 1 second fade-in duration
    const startTime = performance.now();

    const fade = () => {
      const elapsedTime = performance.now() - startTime;
      const progress = Math.min(elapsedTime / fadeDuration, 1); // Calculate progress (0 to 1)

      this.videoMaterial.opacity = progress; // Gradually increase opacity

      if (progress < 1) {
        requestAnimationFrame(fade); // Continue fading in until complete
      }
    };

    fade(); // Start the fade-in animation
  }

  set mirrored(value) {
    this.horizontallyMirrored = value;
    this.texture.center = new THREE.Vector2(0.5, 0.5);
    if (value == true) {
      this.texture.rotation = Math.PI;
      this.texture.flipY = false;
    } else {
      this.texture.rotation = 0;
      this.texture.flipY = true;
    }
  }

  get mirrored() {
    return this.horizontallyMirrored;
  }

  /**
   *
   */
  async start(options = { width: 640, height: 480, brightness: 0.5 }) {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      await this.stopPreviousStreams();

      try {
        const constraints = (window.constraints = {
          audio: false,
          video: {
            width: options.width,
            height: options.height
          }
        });
        const isMobile = AFRAME.utils.device.isMobile();

        if (isMobile) {
          constraints.video = {
            facingMode: "environment",
            width: { ideal: options.width },
            height: { ideal: options.height }
          };
        } else {
          this.mirrored = true;
        }

        this.stream = await navigator.mediaDevices.getUserMedia(constraints);

        this.scene.background = null;
        this.video.srcObject = this.stream;
        this.video.play();
        this.cameraStarted = window.cameraStarted = true;
        window.dispatchEvent(new CustomEvent("ar-camera-ready"));
      } catch (err) {
        console.error(`Capture error ${err.message}`);
        console.error(err);
        console.error(err.name);
        this.cameraStarted = window.cameraStarted = true;
        // AFRAME.scenes[0].object3D.entered = true;
        alert("Couldn't configure camera. Please check your camera permissions and try again.");
      }
    } else {
      console.error("Media devices API not supported");
    }
  }

  async stopPreviousStreams() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => {
        this.stream.removeTrack(track);
        track.stop();
      });

      this.stream = null;
    }
  }

  /**
   *
   */
  stop() {
    this.video.pause();
    const videoTrack = this.stream.getVideoTracks()[0];
    videoTrack?.stop();
    this.cameraStarted = false;
  }

  /**
   *
   */
  update() {
    this.captureRenderer.render(this.captureScene, this.cameraWebcam); // Render the capture scene with the video texture
  }

  /**
   *
   * @param {*} w
   * @param {*} h
   */
  setBrightness(brightness) {
    this.texture.colorSpace = THREE.SRGBColorSpace;
    this.texture.format = THREE.RGBAFormat;
    this.texture.minFilter = THREE.LinearFilter;
    this.texture.magFilter = THREE.LinearFilter;
    this.texture.needsUpdate = true;

    // Create a material with brightness adjustment
    this.brightnessMaterial = new THREE.MeshBasicMaterial({
      map: this.texture,
      color: new THREE.Color(brightness, brightness, brightness)
    });

    // Apply the material to a mesh and render it to a render target
    const renderTarget = new THREE.WebGLRenderTarget(this.texture.image.videoWidth, this.texture.image.videoHeight);
    const brightnessScene = new THREE.Scene();
    const brightnessCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    const brightnessMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.brightnessMaterial);
    brightnessScene.add(brightnessMesh);

    // Render the scene with brightness adjustment
    this.mainRenderer.setRenderTarget(renderTarget);
    this.mainRenderer.render(brightnessScene, brightnessCamera);
    this.mainRenderer.setRenderTarget(null);

    // Update the video texture with the adjusted brightness
    this.texture = renderTarget.texture;
  }

  fitToSize(w, h) {
    const ios = isIOS();
    const targetWidth = w;
    const targetHeight = ios ? h + 40 : h;
    let targetAspectRatio = 1;

    if (targetWidth < targetHeight) {
      targetAspectRatio = targetWidth / targetHeight;
      const scaleFactor = this.aspectRatioH / targetAspectRatio;
      this.videoMesh.scale.set(1 * scaleFactor, 1, 1);
    } else {
      targetAspectRatio = targetHeight / targetWidth;
      const scaleFactor = this.aspectRatioV / targetAspectRatio;
      this.videoMesh.scale.set(1, 1, 1);
    }

    this.videoMesh.updateMatrix();
  }

  resize() {
    const w = window.innerWidth;
    const h = isIOS() ? screen.height : window.innerHeight;
    this.captureRenderer.setSize(w, h);
    this.fitToSize(w, h);
  }

  forceRendererResize() {
    const width = window.innerWidth;
    const height = isIOS() ? screen.height : window.innerHeight;

    // Update renderer sizes
    this.captureRenderer.setSize(width, height);

    // Update camera aspect ratios
    this.cameraWebcam.aspect = width / height;
    this.cameraWebcam.updateProjectionMatrix();

    // Adjust the video mesh size to fit the new dimensions
    this.fitToSize(width, height);
  }

  /**
   *
   */
  dispose() {
    this.stop();
    this.texture.dispose();
    this.video.replaceWith(this.video.cloneNode(true));
  }

  /**
   *
   */
  static async enumerateDevices() {
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
        let devices = [];
        stream.getVideoTracks().forEach(track => {
          devices.push({ id: track.id, name: track.label });
        });
        return devices;
      } catch (err) {
        console.error(`enumerateDevices error ${err.message}`);
      }
    } else {
      console.error("Media devices API not supported");
    }
  }
}

export { CameraCapture };
